import type {
  CyFunCategory,
  CyFunRequirement,
  CyFunSubcategory,
  QuestionnaireItem,
} from "@/types/maturity";

function avg(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function computePreliminaryScores(
  responses: Record<string, boolean>,
  items: QuestionnaireItem[],
  categories: CyFunCategory[]
): Record<string, number> {
  const catMap: Record<string, { total: number; checked: number }> = {};

  for (const cat of categories) {
    catMap[cat.code] = { total: 0, checked: 0 };
  }

  for (const item of items) {
    for (const catCode of item.linked_category_codes) {
      if (!catMap[catCode]) continue;
      catMap[catCode].total++;
      if (responses[String(item.item_index)] === true) {
        catMap[catCode].checked++;
      }
    }
  }

  const scores: Record<string, number> = {};
  for (const [code, counts] of Object.entries(catMap)) {
    scores[code] =
      counts.total > 0 ? 1.0 + (counts.checked / counts.total) * 1.5 : 1.0;
  }

  return scores;
}

export function computePreliminaryOverallScore(
  responses: Record<string, boolean>,
  items: QuestionnaireItem[]
): number {
  const totalItems = items.length;
  if (totalItems === 0) return 0;
  const totalChecked = items.filter(
    (item) => responses[String(item.item_index)] === true
  ).length;
  return (totalChecked / totalItems) * 2.5;
}

export function computeFullScores(
  responses: Record<string, number>,
  requirements: CyFunRequirement[],
  subcategories: CyFunSubcategory[],
  categories: CyFunCategory[]
): {
  subcategoryScores: Record<string, { doc: number; impl: number }>;
  categoryScores: Record<string, number>;
  overallScore: number;
} {
  const subcategoryScores: Record<string, { doc: number; impl: number }> = {};

  for (const subcat of subcategories) {
    const subcatReqs = requirements.filter(
      (r) => r.subcategory_code === subcat.code
    );
    if (subcatReqs.length === 0) continue;

    const docScores: number[] = [];
    const implScores: number[] = [];

    for (const req of subcatReqs) {
      const d = responses[`${req.code}-D`];
      const i = responses[`${req.code}-I`];
      if (d !== undefined) docScores.push(d);
      if (i !== undefined) implScores.push(i);
    }

    if (docScores.length > 0 || implScores.length > 0) {
      subcategoryScores[subcat.code] = {
        doc: docScores.length > 0 ? avg(docScores) : 0,
        impl: implScores.length > 0 ? avg(implScores) : 0,
      };
    }
  }

  const categoryScores: Record<string, number> = {};

  for (const cat of categories) {
    const catSubcats = subcategories.filter(
      (s) => s.category_code === cat.code
    );
    const applicable = catSubcats.filter(
      (s) => subcategoryScores[s.code] !== undefined
    );
    if (applicable.length === 0) continue;

    const K = avg(applicable.map((s) => subcategoryScores[s.code].doc));
    const L = avg(applicable.map((s) => subcategoryScores[s.code].impl));
    categoryScores[cat.code] = (K + L) / 2;
  }

  const overallScore =
    Object.values(categoryScores).length > 0
      ? avg(Object.values(categoryScores))
      : 0;

  return { subcategoryScores, categoryScores, overallScore };
}

export function validateAssuranceLevel(
  responses: Record<string, number>,
  keyMeasures: string[],
  categoryScores: Record<string, number>,
  overallScore: number,
  keyMeasureMinimum: number = 3
): {
  keyMeasuresPassed: boolean;
  categoriesPassed: boolean;
  totalPassed: boolean;
  levelAchieved: boolean;
  failedKeyMeasures: string[];
  failedCategories: string[];
} {
  const failedKeyMeasures: string[] = [];
  for (const mc of keyMeasures) {
    const d = responses[`${mc}-D`];
    const i = responses[`${mc}-I`];
    if (d === undefined || i === undefined) {
      failedKeyMeasures.push(mc);
      continue;
    }
    if ((d + i) / 2 < keyMeasureMinimum) failedKeyMeasures.push(mc);
  }

  const failedCategories: string[] = [];
  for (const [code, score] of Object.entries(categoryScores)) {
    if (score < 3) failedCategories.push(code);
  }

  const keyMeasuresPassed = failedKeyMeasures.length === 0;
  const categoriesPassed = failedCategories.length === 0;
  const totalPassed = overallScore >= 3.5;

  return {
    keyMeasuresPassed,
    categoriesPassed,
    totalPassed,
    levelAchieved: keyMeasuresPassed && categoriesPassed && totalPassed,
    failedKeyMeasures,
    failedCategories,
  };
}

export function computeDomainScore(
  categoryScores: Record<string, number>,
  categories: CyFunCategory[],
  domainCode: string
): number {
  const domainCats = categories.filter((c) => c.activity_code === domainCode);
  const scores = domainCats
    .filter((c) => categoryScores[c.code] !== undefined)
    .map((c) => categoryScores[c.code]);
  return scores.length > 0 ? avg(scores) : 0;
}

const ASSURANCE_HIERARCHY: Record<string, string[]> = {
  "de Bază": ["de Bază"],
  Important: ["Important"],
  Esențial: ["Esențial"],
};

export function getApplicableRequirements(
  requirements: CyFunRequirement[],
  assuranceLevel: string
): CyFunRequirement[] {
  const allowed = ASSURANCE_HIERARCHY[assuranceLevel];
  if (!allowed) return requirements;
  return requirements.filter((r) => allowed.includes(r.assurance_level));
}

export function getAnsweredRequirementCount(
  responses: Record<string, number>,
  applicableRequirements: CyFunRequirement[]
): number {
  return applicableRequirements.filter(
    (req) =>
      responses[`${req.code}-D`] !== undefined &&
      responses[`${req.code}-I`] !== undefined
  ).length;
}

export function getTotalQuestionCount(
  applicableRequirements: CyFunRequirement[]
): number {
  return applicableRequirements.length * 2;
}
